import React, { Component, Fragment } from 'react';
import get from 'lodash/get';
import * as Sentry from '@sentry/browser';
import Loading from 'components/Loading/Loading';
import PaymentForm from 'components/forms/PaymentForm';
import SatisfactionGuarantee from 'components/SatisfactionGuarantee';
import SecuredCheckout from 'components/SecuredCheckout';
import { browserHistory, withRouter } from 'react-router';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { SubmissionError } from 'redux-form';
import { injectStripe } from 'react-stripe-elements';
import { addStripeCardAsync } from 'actions/stripeCardActions';
import { createMembership } from 'actions/stripeSubscriptionActions';
import { createStripeCustomerAsync } from 'actions/stripeCustomerActions';
import { makePayment } from 'actions/stripePaymentActions';
import { signup, purchaseTrialCredit } from 'actions/userActions';
import PurchaseTotal from 'components/PurchaseTotal';
import PromoCodeSpot from 'components/PromoCodeSpot';

import '../ActivateSubscription/style.css';

class ActivateCreditTrial extends Component {
  constructor(props) {
    super(props);

    this.state = {
      showCycleSelect: true,
      promo: null,
      isActivating: false,
      email: get(this.props, 'location.query.email', ''),
      error: null,
    };
  }

  componentDidUpdate(prevProps) {
    if (
      prevProps.user.loginStatus === 'LOADING' &&
      this.props.user.loginStatus === 'SUCCESS'
    ) {
      this.handleActivate();
    }
  }

  isLoggedIn = () => {
    return (
      this.props.user &&
      (this.props.user.reauthenticateStatus === 'SUCCESS' ||
        this.props.user.loginStatus === 'SUCCESS')
    );
  };

  login = async () => {
    const { email } = this.state;
    try {
      await this.props.signup(
        email,
        null,
        null,
        this.props.bookingData,
        null,
        (response) => {
          Sentry.captureMessage(
            `Login on activate page failed for email: ${email}.`,
            response.message
          );
        }
      );
    } catch (err) {
      throw new SubmissionError(err);
    }
  };

  getOrCreateStripeCustomer = async (user) => {
    const email = get(user, 'profile.email');
    const stripeCustomerId = get(user, 'profile.stripeCustomerId');
    if (stripeCustomerId) return stripeCustomerId;
    const stripeCustomerResponse = await this.props.createStripeCustomerAsync(
      email
    );
    return stripeCustomerResponse.stripeCustomerId;
  };

  handleActivate = async () => {
    if (!this.isLoggedIn()) {
      await this.login();
      return;
    }

    this.setState({
      isActivating: true,
      error: null,
    });

    const { user, stripe } = this.props;
    const email = get(user, 'profile.email');

    try {
      const tokenResponse = await stripe.createToken();
      const { token } = tokenResponse;

      if (!token) {
        this.setState({
          isActivating: false,
          error: 'Internal payment issue, please try again later.',
        });
        Sentry.captureMessage(
          `Stripe token creation failed for email: ${email}`
        );
        return;
      }

      const stripeCustomerId = await this.getOrCreateStripeCustomer(user);
      if (!stripeCustomerId) {
        this.setState({
          isActivating: false,
          error: 'Internal payment issue, please try again later.',
        });
        Sentry.captureMessage(
          `Could not get or create stripe customer for email: ${email}`
        );
        return;
      }

      await this.props.addStripeCardAsync(stripeCustomerId, token.id, false);

      await this.props.purchaseTrialCredit(this.state.promo);

      browserHistory.push('signup-details');
    } catch (ex) {
      console.log(ex);
      this.setState({
        error: 'Unexpected error',
        isActivating: false,
      });
      Sentry.captureException(ex);
    }
  };

  updateEmail = (e) => {
    this.setState({
      email: e.target.value,
    });
  };

  onApplyPromoCode = (promo) => {
    this.setState({ promo });
  };

  onClearPromoCode = () => {
    this.setState({ promo: null });
  };

  render() {
    const {
      user, stripeCards, payment, subscriptionError
    } = this.props;

    let error;
    if (subscriptionError) {
      error = get(subscriptionError, 'message');
    } else if (stripeCards.edit_error) {
      error = get(stripeCards, 'edit_error');
    } else if (payment.error) {
      error = payment.error;
    } else if (user && user.error && user.error.message) {
      error = user.error.message;
    } else {
      error = this.state.error;
    }

    return (
      <div className="activate-content mx-auto my-5">
        <div className="row d-block">
          <div className="col activate-ms-content--wrapper">
            <div className="activate-ms-content activate-ms-content--activate text-center">
              <section className="activate-ms-content__section activate-ms-content__section--billing text-left">
                <h2 className="activate-content__title" data-qa-id="trial-payment-title">Trial Lesson</h2>
                <p className="activate-content__sub-title">
                  <span>Book a 30-minute trial lesson, 100% risk free</span>
                </p>
                {!this.isLoggedIn() && (
                  <Fragment>
                    <div className="row">
                      <div className="form-group col-12 mb-4">
                        <label className="form-control-label" htmlFor="email">
                          Email
                        </label>
                        <input
                          name="email"
                          className="form-control"
                          type="text"
                          placeholder="abc@gmail.com"
                          value={this.state.email}
                          onChange={this.updateEmail}
                        />
                      </div>
                    </div>
                  </Fragment>
                )}
                <div className="activate-ms-content__tooltip-wrapper">
                  <p className="form-control-label m-0">Payment information</p>
                </div>

                <div className="mb-2">
                  <PaymentForm hidePostalCode hasCards hideFieldName />
                </div>

                <PurchaseTotal promo={this.state.promo} originalCents={500} />

                <p className="activate-ms-content__section-desc my-3">
                  By clicking the button below you agree to our{' '}
                  <a
                    href="https://www.rypeapp.com/terms-of-use"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Terms of Service
                  </a>
                </p>
              </section>
              <div>
                {this.state.isActivating ? (
                  <Loading size="small" />
                ) : (
                  <Fragment>
                    <button
                      type="button"
                      className="activate-ms-content__submit-btn btn btn-primary btn-cursor w-100"
                      onClick={this.handleActivate}
                      data-qa-id="claim-trial-lesson-button"
                    >
                      Claim Trial Lesson
                    </button>
                    {error && (
                      <p className="text-danger my-1">
                        <small>{error}</small>
                      </p>
                    )}
                  </Fragment>
                )}
              </div>
              <SecuredCheckout className="activate-ms-content__secure-text" />
            </div>

            <div className="activate-ms-content activate-ms-content--benefits mb-4">
              <PromoCodeSpot
                initialPromoCode={this.props.location.query.promo}
                onApplyPromoCode={this.onApplyPromoCode}
                onClearPromoCode={this.onClearPromoCode}
              />
            </div>

            <SatisfactionGuarantee dark />
          </div>
        </div>
      </div>
    );
  }
}

function mapStateToProps(state) {
  return {
    user: state.user,
    stripeCards: state.stripeCards,
    subscriptionError: state.subscription.error,
    payment: state.stripePayment,
  };
}

export default compose(
  withRouter,
  injectStripe,
  connect(mapStateToProps, {
    addStripeCardAsync,
    createMembership,
    createStripeCustomerAsync,
    makePayment,
    signup,
    purchaseTrialCredit,
  })
)(ActivateCreditTrial);



// WEBPACK FOOTER //
// ./src/pages/student/Activate/ActivateCreditTrial/index.js